import React from 'react';
import NdsThemeProvider from '@ntuctech/devex-tangram/Theme/NdsThemeProvider';
import { SvgClose } from '@ntuctech/devex-tangram/Icon';
import Grid from '@ntuctech/devex-tangram/Grid';
import styled from 'styled-components';
import Modal from '@ntuctech/devex-tangram/Modal';
import ModalTitle from '@ntuctech/devex-tangram/ModalTitle';
import ModalContent from '@ntuctech/devex-tangram/ModalContent';
import ModalActions from '@ntuctech/devex-tangram/ModalActions';
import Typography from '@ntuctech/devex-tangram/Typography';
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained';
import { ButtonOutlined, ButtonIcon } from '@ntuctech/devex-tangram/Button';
import { useRouter } from 'next/router';
import moment from 'moment';
import Text from '../../Text/Text';
import Loader from '../../Theme/Loader';
import { useFeatureFlag } from '../../FeatureFlagProvider';
import { CreateGuestGift, patchUpdateGuest } from '../../../services/api/api';
import MobileModelComponent from './MobileModel';
import { numberFormat } from '../../../lib/utils';
import ErrorUnauthorized from '../../../services/fetch/errorUnauthorized';
import {
  CATEGORY,
  GTM_ENV_VAR as GEV,
  PAGE_TYPES,
  SCREEN_NAME,
  GIFTING_AMP_NAME,
} from '../../../lib/eventTrackerFunction';
import { AMP_EVENT_NAME } from '../../../lib/amplitude';
import { SCREEN_SIZE } from '../../../lib/mediaQuery';
import CancelModal from './CancelModal';
import { useAppContext } from '../../../lib/contextProviders/contexts/appContext';
import { useCartContext } from '../../../lib/contextProviders/contexts/cartContext';
import { useUserContext } from '../../../lib/contextProviders/contexts/userContext';

export const EVENT_PROPERTY = 'Event Property';
const RightWhiteBox = styled.div`
  .no-spacing {
    padding: 0;
    margin: 0;
  }
  background-color: #fff;
  padding: 2rem;
  border-radius: 0.5rem;
  @media screen and (min-width: 600px) {
    border: 1px solid #eaeaea;
  }
  ${SCREEN_SIZE.Only.Tablet} {
    padding: 1.5rem !important;
  }
  ${SCREEN_SIZE.Below.MobileL} {
    padding: 1rem;
    border-radius: 0;
    border: 0;
  }
`;
const BoxTitle = styled.h5`
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 28px;
  color: #333333;
  margin: 0;
`;

const WebView = styled.div`
  display: none;
  @media screen and (min-width: 767px) {
    display: block;
  }
`;
const ImgSection = styled.div`
  margin-top: 15px;
  img {
    width: 100%;
    border-radius: 8px 8px 8px 8px;
  }
`;
const MessageTxt = styled.div`
  margin-top: 15px;
  p {
    font-style: normal;
    margin-bottom: 0;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #333333;
  }
  span {
    font-weight: 700;
    display: block;
  }
`;
const VoucherBal = styled.p`
  margin-bottom: 0;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #333333;
  margin-top: 0;
`;
const Card = styled.div`
  background-color: #fff;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
  height: auto;
`;
const CardList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 10px;
`;
const CardImg = styled.div`
  img {
    width: 100%;
    height: 196px;
    object-fit: contain;
    border-bottom-right-radius: 10px;
    border-bottom-left-radius: 10px;
  }
`;
const CardTxt = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 10px;
  span {
    max-height: 100px;
    overflow-y: auto;
  }
`;
const MobilePreMsg = styled.p`
  font-family: 'Lato', serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #333333;
  width: 100%;
  margin: 0;
  padding: 6px 0;
`;

const CardBalance = styled.div`
  margin: 0;
  padding: 10px 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;
const ModalHeader = styled.div`
  padding-bottom: 15px;
`;
const GridSection = styled.div`
  > div:nth-child(1) {
    padding: 0 0;
  }
  > div:nth-child(1) div:nth-child(1) {
    padding-left: 0;
  }
  > div:nth-child(1) div:nth-child(2) {
    padding-right: 0;
    text-align: right;
  }
`;

const BetweenLine = styled.span`
  display: block;
  height: 1px;
  background: #eaeaea;
  margin: 20px 0;
`;

export const PreviewModel = (props) => {
  const { open, closeModal, voucherData, isEditMode, voucherMetaData, isB2bEnabled } = props;

  const findCategory = (value) => {
    const imgindex = voucherMetaData.findIndex((d) => {
      return d.png === value;
    });
    let category;
    if (imgindex >= 0) {
      category = voucherMetaData[imgindex].name;
    }
    return category;
  };

  const amountBusiness = voucherData.bussinessData?.reduce((acc, key) => {
    return acc + key.value * key.qty;
  }, 0);

  return (
    <Modal id="sampleId" open={open}>
      <GridSection>
        <Grid container>
          <Grid item xs={10}>
            <ModalTitle>
              <ModalHeader>
                <Text fontFamily="Lato" size="xxl" weight="bold">
                  Preview
                </Text>
              </ModalHeader>
            </ModalTitle>
          </Grid>
          <Grid item xs={2}>
            <ButtonIcon size="s" onClick={closeModal}>
              <SvgClose size="l" color="#1557BF" data-testid="closePreview" />
            </ButtonIcon>
          </Grid>
        </Grid>
      </GridSection>
      <ModalContent>
        <Typography>
          {voucherData && voucherData.metadata.category && (
            <Text fontFamily="Lato" size="large" weight="bold">
              {findCategory(voucherData.metadata.image)}
            </Text>
          )}
        </Typography>
        <CardList>
          <Card>
            <CardImg>
              <img src={voucherData && voucherData.metadata.image} alt="rover" />
            </CardImg>
            {isEditMode === true ? (
              <CardBalance>
                <Text fontFamily="Lato" size="large" weight="bold">
                  Voucher value:{' '}
                  {numberFormat(
                    Number(
                      amountBusiness || voucherData?.amount / voucherData.quantity,
                      10 || 0
                    ).toFixed(2)
                  )}
                </Text>
              </CardBalance>
            ) : (
              <CardBalance>
                {!isB2bEnabled && (
                  <Text fontFamily="Lato" size="large" weight="bold">
                    Voucher value:{' '}
                    {numberFormat(
                      Number(amountBusiness || voucherData.amount ? voucherData.amount : 0).toFixed(
                        2
                      )
                    )}
                  </Text>
                )}
              </CardBalance>
            )}
            <CardTxt>
              <MobilePreMsg>Message:</MobilePreMsg>
              {voucherData && voucherData.metadata.message && (
                <Text fontFamily="Lato" size="large" weight="regular">
                  {' '}
                  {voucherData.metadata.message}
                </Text>
              )}
            </CardTxt>
          </Card>
        </CardList>
      </ModalContent>
      <ModalActions>
        <ButtonContained onClick={closeModal}>
          <Text fontFamily="Lato" size="large" weight="bold">
            Close
          </Text>
        </ButtonContained>
      </ModalActions>
    </Modal>
  );
};

const VoucherBalance = (props) => {
  const { Total, amount, TotalQty } = props;
  return (
    <>
      <BoxTitle>Total: {Total}</BoxTitle>
      <VoucherBal>
        Voucher value:
        {amount} | Qty: {TotalQty}
      </VoucherBal>
    </>
  );
};
const commanEvent = {
  [GEV.PAGE_TYPE]: PAGE_TYPES.CUSTOMISE,
  [GEV.SCREEN_NAME]: SCREEN_NAME.CUSTOMISE,
  [GEV.EVENT_CAT]: CATEGORY.VOUCHER,
};

const WebViewModel = (props) => {
  const { voucherData, isEditMode, isEnabled, saveButton, addToCart, isEnabledSaveBtn, track } =
    props;
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isOkay, setIsOkay] = React.useState(false);

  const TotalQty = voucherData?.receivers
    ?.map((item) => item.count)
    .reduce((a, b) => Number(a) + Number(b), 0);
  const TotalValue = parseInt(
    parseInt(voucherData?.amount / voucherData?.quantity, 10) *
      (TotalQty || voucherData?.receivers?.length),
    10
  );
  /* eslint-disable radix */
  const homepage = '/';

  React.useEffect(() => {
    if (isOkay) {
      setIsModalOpen(false);
      window.location.href = homepage;
    }
  }, [isOkay]);

  return (
    <WebView>
      <CancelModal
        open={isModalOpen}
        track={track}
        onClose={() => setIsModalOpen(false)}
        setIsOkay={setIsOkay}
      />
      <RightWhiteBox>
        <Grid container data-testid="preview" className="no-spacing">
          <Grid item xs={12} className="no-spacing">
            <BoxTitle>Preview</BoxTitle>
          </Grid>
          <Grid item xs={12} className="no-spacing">
            <ImgSection>
              <img alt="" src={voucherData.metadata.image} />
            </ImgSection>
            <MessageTxt>
              <p>
                <span>Message:</span>
                {voucherData && voucherData.metadata.message}
              </p>
            </MessageTxt>
          </Grid>
        </Grid>
        <BetweenLine />
        <Grid container className="no-spacing">
          <Grid item xs={12} className="no-spacing">
            {isEditMode === true ? (
              <VoucherBalance
                Total={numberFormat(TotalValue.toFixed(2))}
                amount={numberFormat(
                  parseInt(Number(voucherData?.amount / voucherData?.quantity, 10) || 0).toFixed(2)
                )}
                TotalQty={TotalQty || voucherData?.receivers?.length}
              />
            ) : (
              <VoucherBalance
                Total={numberFormat(
                  Number(
                    voucherData?.amount * (TotalQty || voucherData?.receivers?.length) || 0
                  ).toFixed(2)
                )}
                amount={numberFormat(Number(voucherData?.amount || 0).toFixed(2))}
                TotalQty={TotalQty || voucherData?.receivers?.length}
              />
            )}
            {isEditMode === true ? (
              <ButtonContained
                size="m"
                disabled={!isEnabledSaveBtn}
                fullWidth
                data-testid="save-button"
                onClick={() => saveButton()}
              >
                Save
              </ButtonContained>
            ) : (
              <ButtonContained
                size="m"
                data-testid="Add-cart-button"
                disabled={!isEnabled}
                fullWidth
                onClick={() => {
                  addToCart();
                }}
              >
                Add to cart
              </ButtonContained>
            )}
            <ButtonOutlined
              size="m"
              fullWidth
              data-testid="cancel-button"
              onClick={() => {
                if (voucherData.amount && !isEditMode) {
                  setIsModalOpen(true);
                } else {
                  window.location.href = '/cart/';
                }
              }}
            >
              Cancel
            </ButtonOutlined>
          </Grid>
          <Grid item xs={12} />
        </Grid>
      </RightWhiteBox>
    </WebView>
  );
};
export const errorHandler = (setErrorBanner, messageNum) => {
  setErrorBanner({
    show: true,
    message: messageNum,
  });
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const errorBannerFalse = (setErrorBanner) => {
  setErrorBanner({
    show: false,
    message: 0,
  });
};

const payLoadReq = (voucherData) => {
  return {
    sender: voucherData?.sender,
    message: voucherData?.metadata?.message,
    deliveryTime: moment.utc(voucherData?.metadata?.deliveryTime),
    metadata: {
      catergory: voucherData?.metadata?.catergory,
      image: voucherData?.metadata?.image,
      name: voucherData?.metadata?.name,
    },
    receivers:
      voucherData?.receivers &&
      voucherData?.receivers.map((item) => {
        let deliveryType;
        if (item.deliveryMethod === 'email') {
          deliveryType = { email: item.email };
        } else {
          deliveryType = { phone: item.phone };
        }
        return {
          name: item.name,
          deliveryTime: moment.utc(voucherData?.deliveryTime),
          amount: `${parseInt(voucherData?.amount / voucherData?.quantity, 10)}`,
          deliveryMethod: item.deliveryMethod,
          count: parseInt(item.count),
          ...deliveryType,
        };
      }),
  };
};

const Preview = (props) => {
  const { isReady } = useFeatureFlag();
  const [open, setOpen] = React.useState(false);
  const router = useRouter();
  const {
    isEnabled,
    voucherData,
    isEditMode,
    setIsEditMode,
    voucherMetaData,
    isEnabledSaveBtn,
    isKeyboardOpen,
    track,
    trackEvents,
    setErrorBanner,
  } = props;
  /* eslint-disable radix */
  const payload = payLoadReq(voucherData);
  const {
    appState: { siteType },
  } = useAppContext();
  const { fetchCartData } = useCartContext();
  const {
    userState: { guest, auth0Token },
  } = useUserContext();

  const cartPage = '/cart/';

  const openModal = React.useCallback(() => {
    setOpen(true);
    track({
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.PREVIEW,
      [GEV.EVENT_CAT]: 'Egifting',
      [GEV.MEMBER_TYPE]: siteType,
      [GEV.AMPLITUDE_PROPERTY_TYPE]: EVENT_PROPERTY,
      vertical: 'egifting',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = React.useCallback(() => {
    setOpen(false);
  }, []);

  const trackAmplitudeEvent = {
    [GEV.AMPLITUDE_VERTICAL]: AMP_EVENT_NAME.VERTICAL,
    [GEV.AMPLITUDE_GIFTING_SEND_VIA]: voucherData?.receivers[0].deliveryMethod,
    [GEV.AMPLITUDE_GIFTING_MESSAGE]: voucherData?.receivers?.metadata?.message,
    [GEV.AMPLITUDE_GIFTING_DATE_OF_DELIVERY]: voucherData?.receivers?.metadata?.deliveryTime,
    [GEV.AMPLITUDE_GIFTING_CATEGORY]: voucherData?.receivers?.metadata?.all,
    [GEV.AMPLITUDE_GIFTING_CARD_DESIGN]: voucherData?.receivers?.metadata?.image,
    [GEV.AMPLITUDE_GIFTING_MULTIPLE_RECIPIENTS]: voucherData?.receivers?.receivers?.length > 0,
    [GEV.AMPLITUDE_GIFTING_FUTURE_DATE]:
      new Date(voucherData?.receivers?.metadata?.deliveryTime) > new Date(),
  };

  const saveButton = () => {
    let savebutton;
    errorBannerFalse(setErrorBanner);
    const TotalQty = voucherData?.receivers
      ?.map((item) => item.count)
      .reduce((a, b) => Number(a) + Number(b), 0);
    if (TotalQty > 999) {
      errorHandler(setErrorBanner, 1);
      return;
    }
    if (!auth0Token) {
      savebutton = patchUpdateGuest(guest.id, voucherData.id, guest.signature, payload);
    } else {
      savebutton = patchUpdateGuest('', voucherData.id, '', payload, auth0Token);
    }
    savebutton
      .then(async (res) => {
        if (res.code === 200) {
          setIsEditMode(false);
          fetchCartData();
          await router.push(cartPage);
        }
      })
      .catch((error) => {
        errorHandler(setErrorBanner, 2);
        if (auth0Token && error instanceof ErrorUnauthorized) {
          console.log(error, 'error saving cart');
        } else {
          // eslint-disable-next-line no-console
          console.log(error);
        }
      });

    track({
      [GEV.EVENT_ACTION]: 'click',
      [GEV.EVENT_LABEL]: 'gifting_cart_edit_save',
      [GEV.MEMBER_TYPE]: siteType,
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.EDIT_SAVED,
      [GEV.EVENT_CAT]: 'Egifting',
      [GEV.AMPLITUDE_CTA_LOCATION]: 'egifting | edit order',
      [GEV.AMPLITUDE_CTA_SUB_LOCATION]: 'egifting | edit order',
      [GEV.AMPLITUDE_PROPERTY_TYPE]: EVENT_PROPERTY,
      ...trackAmplitudeEvent,
      ...commanEvent,
    });
  };

  const cancelButon = () => {
    setIsEditMode(false);
    router.push(cartPage);
    track({
      [GEV.EVENT_ACTION]: 'click',
      [GEV.EVENT_LABEL]: 'gifting_cart_edit_cancel',
      [GEV.MEMBER_TYPE]: siteType,
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.EDIT_CANCEL,
      [GEV.EVENT_CAT]: 'Egifting',
      [GEV.AMPLITUDE_PROPERTY_TYPE]: EVENT_PROPERTY,
      [GEV.AMPLITUDE_CTA_LOCATION]: 'egifting | edit cart',
      [GEV.AMPLITUDE_CTA_SUB_LOCATION]: 'egifting | edit cart',
      ...trackAmplitudeEvent,
      ...commanEvent,
    });
  };

  const addToCart = () => {
    let addcart;
    errorBannerFalse(setErrorBanner);
    const TotalQty = voucherData?.receivers
      ?.map((item) => item.count)
      .reduce((a, b) => Number(a) + Number(b), 0);
    if (TotalQty > 999) {
      errorHandler(setErrorBanner, 1);
      return;
    }
    if (!auth0Token && guest) {
      addcart = CreateGuestGift(guest.id, payload, guest.signature);
    } else {
      addcart = CreateGuestGift('', payload, '', auth0Token);
    }
    addcart
      .then((res) => {
        if (res.code === 200) {
          fetchCartData();
          router.push(cartPage);
        }
      })
      .catch((error) => {
        errorHandler(setErrorBanner, 2);
        if (auth0Token && error instanceof ErrorUnauthorized) {
          console.log(error, 'error adding to cart');
        }
        trackEvents();
      });
    track({
      [GEV.EVENT_ACTION]: 'click',
      [GEV.EVENT_LABEL]: 'gifting_add_to_cart',
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.ADD_TO_CART,
      [GEV.EVENT_CAT]: 'Egifting',
      [GEV.AMPLITUDE_PROPERTY_TYPE]: EVENT_PROPERTY,
      [GEV.AMPLITUDE_VERTICAL]: GIFTING_AMP_NAME.GIFTING,
      [GEV.AMPLITUDE_CTA_LOCATION]: 'egifting | customize selection',
      [GEV.AMPLITUDE_CTA_SUB_LOCATION]: 'egifting | customize selection',
      [GEV.AMPLITUDE_GIFTING_SEND_VIA]: voucherData?.receivers[0].deliveryMethod,
      [GEV.AMPLITUDE_GIFTING_RECIPIENT]:
        voucherData?.receivers[0].email || voucherData?.receivers[0].phone,
      [GEV.AMPLITUDE_GIFTING_MESSAGE]: voucherData?.metadata?.message,
      [GEV.AMPLITUDE_GIFTING_DATE_OF_DELIVERY]: voucherData?.metadata?.deliveryTime,
      [GEV.AMPLITUDE_GIFTING_CATEGORY]: voucherData?.metadata?.all,
      [GEV.AMPLITUDE_GIFTING_CARD_DESIGN]: voucherData?.metadata?.image,
      [GEV.AMPLITUDE_GIFTING_MULTIPLE_RECIPIENTS]: voucherData?.receivers?.length > 1,
      [GEV.AMPLITUDE_GIFTING_FUTURE_DATE]:
        new Date(voucherData?.metadata?.deliveryTime) > new Date(),
      [GEV.AMPLITUDE_GIFTING_MESSAGE_CATEGORY]:
        voucherMetaData.filter((item) => item.message === voucherData?.metadata?.message).length > 0
          ? 'default'
          : 'custom',
      ...commanEvent,
    });
  };

  // await till flag are loaded
  if (!isReady) {
    return <Loader />;
  }

  return (
    <NdsThemeProvider>
      <WebViewModel
        voucherData={voucherData}
        isEditMode={isEditMode}
        isEnabled={isEnabled}
        cancelButon={cancelButon}
        saveButton={saveButton}
        addToCart={addToCart}
        isEnabledSaveBtn={isEnabledSaveBtn}
        voucherMetaData={voucherMetaData}
        track={track}
      />
      <MobileModelComponent
        voucherData={voucherData}
        isEditMode={isEditMode}
        isEnabled={isEnabled}
        cancelButon={cancelButon}
        saveButton={saveButton}
        addToCart={addToCart}
        openModal={openModal}
        isEnabledSaveBtn={isEnabledSaveBtn}
        isKeyboardOpen={isKeyboardOpen}
      />
      <PreviewModel
        open={open}
        voucherData={voucherData}
        isEditMode={isEditMode}
        closeModal={closeModal}
        voucherMetaData={voucherMetaData}
      />
    </NdsThemeProvider>
  );
};

export default Preview;
